import { useState, useEffect } from "react";
import { Amplify } from "aws-amplify";
import {
  Authenticator,
  ThemeProvider,
  createTheme,
  useAuthenticator,
} from "@aws-amplify/ui-react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "@aws-amplify/ui-react/styles.css";
import awsConfig from "./aws-config";
import LandingPage from "./LandingPage";
import ComingSoon from "./ComingSoon";
import LifeDesk from "./LifeDesk";

Amplify.configure(awsConfig);

// ── LifeDesk Amplify theme ────────────────────────────────────────────────────
const lifeDeskTheme = createTheme({
  name: "lifedesk",
  tokens: {
    fonts: {
      default: {
        variable: { value: "'DM Sans', 'Segoe UI', sans-serif" },
        static:   { value: "'DM Sans', 'Segoe UI', sans-serif" },
      },
    },
    colors: {
      brand: {
        primary: {
          10:  { value: "#EFF6FF" },
          20:  { value: "#DBEAFE" },
          40:  { value: "#93C5FD" },
          60:  { value: "#3B82F6" },
          80:  { value: "#2563EB" },
          90:  { value: "#1D4ED8" },
          100: { value: "#1E40AF" },
        },
      },
    },
    radii: {
      small:  { value: "6px"  },
      medium: { value: "8px"  },
      large:  { value: "10px" },
      xl:     { value: "12px" },
    },
    components: {
      authenticator: {
        router: {
          borderWidth:  { value: "0"                              },
          boxShadow:    { value: "0 8px 32px rgba(15,42,110,.12)" },
          borderRadius: { value: "14px"                           },
        },
      },
      button: {
        primary: {
          backgroundColor: { value: "#2563EB" },
          _hover: { backgroundColor: { value: "#1D4ED8" } },
        },
      },
      fieldcontrol: {
        borderRadius: { value: "8px"     },
        borderColor:  { value: "#D1D5DB" },
        _focus: {
          borderColor: { value: "#2563EB"                        },
          boxShadow:   { value: "0 0 0 3px rgba(37,99,235,.15)" },
        },
      },
    },
  },
});

// ── Password requirement checklist ───────────────────────────────────────────
// Matches Cognito's exact special character set
const SPECIAL_RE = /[\^$*.\[\]{}()?\-"!@#%&/\\,><':;|_~`+=\s]/;

const CHECKS = [
  { label: "Minimum 8 characters",              test: p => p.length >= 8             },
  { label: "Contains at least 1 number",        test: p => /[0-9]/.test(p)           },
  { label: "Contains at least 1 special character", test: p => SPECIAL_RE.test(p)   },
  { label: "Contains at least 1 uppercase letter",  test: p => /[A-Z]/.test(p)      },
  { label: "Contains at least 1 lowercase letter",  test: p => /[a-z]/.test(p)      },
];

function PasswordChecklist({ password }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: 10,
      padding: "10px 14px",
      marginTop: 8,
      boxShadow: "0 4px 16px rgba(15,42,110,.08)",
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", letterSpacing: 0.5, marginBottom: 6 }}>
        PASSWORD REQUIREMENTS
      </div>
      {CHECKS.map((c, i) => {
        const met = c.test(password);
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "3px 0" }}>
            <span style={{
              width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: met ? "#22C55E" : "#F3F4F6",
              transition: "background .2s",
            }}>
              {met && (
                <svg width="9" height="9" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            <span style={{
              fontSize: 12,
              color: met ? "#166534" : "#6B7280",
              fontWeight: met ? 600 : 400,
              transition: "color .2s",
              textDecoration: met ? "none" : "none",
            }}>
              {c.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Custom sign-up form fields ────────────────────────────────────────────────
// PasswordChecklistWidget is a SEPARATE component so its state updates never
// cause Authenticator.SignUp.FormFields to re-render. It listens for native
// DOM input events so it doesn't touch React's synthetic event chain at all.
function PasswordChecklistWidget() {
  const [password,        setPassword]        = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  useEffect(() => {
    const handler = (e) => {
      if (e.target.name === "password")         setPassword(e.target.value);
      if (e.target.name === "confirm_password") setConfirmPassword(e.target.value);
    };
    // Native capture-phase listener — completely outside React's synthetic event system
    document.addEventListener("input", handler, true);
    return () => document.removeEventListener("input", handler, true);
  }, []);

  const confirmHasText = confirmPassword.length > 0;
  const passwordsMatch = password === confirmPassword;

  return (
    <>
      {password.length > 0 && <PasswordChecklist password={password} />}
      {confirmHasText && (
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          margin: "4px 0 8px", fontSize: 12, fontWeight: 600,
          color: passwordsMatch ? "#166534" : "#DC2626",
        }}>
          <span style={{
            width: 14, height: 14, borderRadius: "50%", flexShrink: 0,
            background: passwordsMatch ? "#22C55E" : "#EF4444",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {passwordsMatch ? (
              <svg width="8" height="8" viewBox="0 0 10 8" fill="none">
                <path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>✕</span>
            )}
          </span>
          {passwordsMatch ? "Passwords match" : "Passwords do not match"}
        </div>
      )}
    </>
  );
}

// SignUpFormFields itself holds NO state, so Authenticator.SignUp.FormFields
// never re-renders due to checklist updates.
function SignUpFormFields() {
  return (
    <>
      <Authenticator.SignUp.FormFields />
      <PasswordChecklistWidget />
    </>
  );
}

// Custom components passed to Authenticator
const authComponents = {
  SignUp: {
    FormFields: SignUpFormFields,
  },
  ConfirmSignUp: {
    Header() {
      return (
        <div style={{ paddingBottom: 4 }}>
          <div style={{ fontWeight: 800, fontSize: 20, color: "#0F172A", marginBottom: 6 }}>
            Check your email
          </div>
          <div style={{ fontSize: 14, color: "#64748B", lineHeight: 1.5 }}>
            We emailed you a verification code. Enter it below to confirm your account.
          </div>
        </div>
      );
    },
  },
};

// ── Auth page top bar ─────────────────────────────────────────────────────────
function AuthNav() {
  const navigate = useNavigate();
  return (
    <div style={{
      background: "linear-gradient(180deg, #0F2A6E 0%, #102D75 100%)",
      height: 58, padding: "0 20px",
      display: "flex", alignItems: "center",
      boxShadow: "0 2px 12px rgba(15,42,110,.15)",
      borderBottom: "1px solid rgba(96,165,250,.15)",
      position: "sticky", top: 0, zIndex: 50,
    }}>
      <button
        onClick={() => navigate("/")}
        title="Back to home"
        style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "none", border: "none", padding: 0,
          cursor: "pointer", fontFamily: "inherit",
        }}
      >
        <img
          src="/images/logo.png"
          alt="LifeDesk"
          style={{ width: 30, height: 30, borderRadius: 7, objectFit: "contain", display: "block" }}
          onError={(e) => {
            e.target.outerHTML = `
              <div style="
                width:30px;height:30px;border-radius:7px;
                border:1.5px dashed rgba(147,197,253,.5);background:rgba(96,165,250,.1);
                display:flex;align-items:center;justify-content:center;
                color:#93C5FD;font-size:8px;font-weight:700;
              ">LOGO</div>
            `;
          }}
        />
        <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.4, color: "#fff" }}>
          LifeDesk
        </span>
      </button>
    </div>
  );
}

// ── Core content — branches on auth state ────────────────────────────────────
function AppContent() {
  const { route, signOut, user } = useAuthenticator(ctx => [ctx.route]);

  if (route === "authenticated") {
    return <LifeDesk user={user} signOut={signOut} />;
  }

  return (
    <div style={{
      minHeight: "100vh", background: "#F8FAFC",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      <AuthNav />
      <div style={{
        display: "flex", justifyContent: "center", alignItems: "center",
        minHeight: "calc(100vh - 58px)", padding: "24px 16px",
      }}>
        <div style={{ width: "100%", maxWidth: 460 }}>
          <Authenticator
            loginMechanisms={["email"]}
            signUpAttributes={[]}
            components={authComponents}
          />
        </div>
      </div>
    </div>
  );
}

// ── Protected route ───────────────────────────────────────────────────────────
function ProtectedApp() {
  return (
    <Authenticator.Provider>
      <ThemeProvider theme={lifeDeskTheme} colorMode="light">
        <AppContent />
      </ThemeProvider>
    </Authenticator.Provider>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/"             element={<LandingPage />} />
        <Route path="/coming-soon"  element={<ComingSoon />} />
        <Route path="/app"          element={<ProtectedApp />} />
        <Route path="*"             element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
